import * as React from "react";

const SWIPE_THRESHOLD_PX = 48;

export interface SwipeDayHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
}

/** Release a captured pointer if the API exists. Guarded because jsdom (and older engines)
 * may not implement it, and releasing a non-captured pointer can throw. */
function releaseCapture(e: React.PointerEvent): void {
  if (typeof e.currentTarget.releasePointerCapture === "function") {
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // Never captured / already released — ignore.
    }
  }
}

/**
 * Horizontal swipe detector for the day pager. Tracks only the start/end pointer
 * position (single-gesture drag, not a running trail) — swipe LEFT (negative dx)
 * advances to the next day, swipe RIGHT (positive dx) goes back a day. A gesture is
 * ignored unless the horizontal delta clears `SWIPE_THRESHOLD_PX` AND horizontal
 * movement dominates vertical (so a vertical scroll never mis-fires a day change).
 *
 * On pointerdown the pointer is *captured* so the terminating pointerup is still delivered
 * to the swipe container even if the finger drifts outside it mid-drag. A `pointercancel`
 * (e.g. the browser claiming the gesture for native scroll) discards the in-flight gesture
 * without firing either callback. Pair with `touch-action: pan-y` on the container so
 * vertical scrolling still works while horizontal drags reach us.
 */
export function useSwipeDay(onPrev: () => void, onNext: () => void): SwipeDayHandlers {
  const startRef = React.useRef<{ x: number; y: number } | null>(null);

  function onPointerDown(e: React.PointerEvent): void {
    startRef.current = { x: e.clientX, y: e.clientY };
    // Guarded: jsdom may not implement setPointerCapture, and it can throw for a
    // non-active pointer id — neither should break the gesture.
    if (typeof e.currentTarget.setPointerCapture === "function") {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // Capture is a reliability nicety, not a correctness requirement — ignore failures.
      }
    }
  }

  // No-op: only the start/end positions matter for this single-swipe gesture, but the
  // handler is still exposed (rather than omitted) so callers can spread a stable,
  // complete set of pointer handlers onto the swipe container.
  function onPointerMove(): void {}

  function onPointerUp(e: React.PointerEvent): void {
    const start = startRef.current;
    startRef.current = null;
    releaseCapture(e);
    if (!start) return;

    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;

    if (Math.abs(dx) > SWIPE_THRESHOLD_PX && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) onNext();
      else onPrev();
    }
  }

  // The gesture was taken over by the browser (native scroll, etc.) — abandon it entirely,
  // firing neither callback, and clear the start so the next gesture begins clean.
  function onPointerCancel(e: React.PointerEvent): void {
    startRef.current = null;
    releaseCapture(e);
  }

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}
